import { useEffect, useState, useRef, useCallback } from 'react';

const log = (message: string, data?: any) => {
  const timestamp = new Date().toLocaleTimeString();
  console.log(`[${timestamp}] 🎤 WHISPER: ${message}`, data || '');
};

const logError = (message: string, error?: any) => {
  const timestamp = new Date().toLocaleTimeString();
  console.error(`[${timestamp}] ❌ WHISPER ERROR: ${message}`, error || '');
};

const logSuccess = (message: string, data?: any) => {
  const timestamp = new Date().toLocaleTimeString();
  console.log(`[${timestamp}] ✅ WHISPER SUCCESS: ${message}`, data || '');
};

export const useWhisper = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);
  const isListeningRef = useRef(false);
  const shouldContinueRef = useRef(false);
  const onTranscriptRef = useRef<any>(null);
  const languageRef = useRef('en-US');

  useEffect(() => {
    log('useWhisper hook mounted');
    setIsLoading(false);
    setError(null);
    logSuccess('Web Speech API ready');
  }, []);

  const startLiveTranscription = useCallback(
    (onTranscript: (text: string, isFinal: boolean) => void, language = 'en-US') => {
      log('Starting live transcription', { language });

      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!SpeechRecognition) {
        const errorMsg = 'Web Speech API not supported';
        logError(errorMsg);
        setError(errorMsg);
        throw new Error(errorMsg);
      }

      onTranscriptRef.current = onTranscript;
      languageRef.current = language;
      shouldContinueRef.current = true;

      const startRecognition = () => {
        const recognition = new SpeechRecognition();
        recognitionRef.current = recognition;

        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = language;

        let lastInterimText = '';

        recognition.onstart = () => {
          logSuccess('Live transcription started');
          isListeningRef.current = true;
        };

        recognition.onresult = (event: any) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            const confidence = event.results[i][0].confidence;

            if (event.results[i].isFinal) {
              // Final result - send to callback
              finalTranscript += transcript + ' ';
              logSuccess('Final result', { transcript, confidence: `${(confidence * 100).toFixed(1)}%` });
            } else {
              // Interim result - accumulate for display only
              interimTranscript += transcript;
            }
          }

          // Send final results
          if (finalTranscript.trim()) {
            if (onTranscriptRef.current) {
              onTranscriptRef.current(finalTranscript.trim(), true);
            }
            lastInterimText = '';
          }

          // Send interim results only for live display (not to parent callback)
          if (interimTranscript && interimTranscript !== lastInterimText) {
            lastInterimText = interimTranscript;
            log('Interim result', { transcript: interimTranscript });
            
            if (onTranscriptRef.current) {
              onTranscriptRef.current(interimTranscript, false);
            }
          }
        };

        recognition.onerror = (event: any) => {
          logError('Recognition error', event.error);
          setError(`Speech recognition error: ${event.error}`);
        };

        recognition.onend = () => {
          logSuccess('Live transcription ended');
          isListeningRef.current = false;

          // Auto-restart if still recording
          if (shouldContinueRef.current) {
            log('Auto-restarting recognition for continuous speech');
            setTimeout(() => {
              startRecognition();
            }, 100);
          }
        };

        try {
          recognition.start();
        } catch (err) {
          logError('Failed to start recognition', err);
        }
      };

      startRecognition();
    },
    []
  );

  const stopLiveTranscription = useCallback(() => {
    log('Stopping live transcription');
    shouldContinueRef.current = false;
    
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      isListeningRef.current = false;
      logSuccess('Live transcription stopped');
    }
  }, []);

  const abortLiveTranscription = useCallback(() => {
    log('Aborting live transcription');
    shouldContinueRef.current = false;
    
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      isListeningRef.current = false;
      logSuccess('Live transcription aborted');
    }
  }, []);

  return {
    startLiveTranscription,
    stopLiveTranscription,
    abortLiveTranscription,
    isLoading,
    error,
    isListening: isListeningRef.current
  };
};
